// src/components/FormLists/useFormListsSpreadsheetSync.js

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  applyFormListsApplyPlan,
  buildFormListsApplyPlanFromSnapshot,
} from './formListsApplyUtils';

const dirtySummaryInitialState = {
  dirtyGroupCount: 0,
  dirtyRowCount: 0,
  dirtyCellCount: 0,
};

/**
 * mergeDropdownOptionOverrides
 * -----------------------------------------------------------------------------
 * Merges dirty option-set overrides into the current context dropdownOptions map.
 *
 * IMPORTANT
 *  • This does not mutate baseDropdownOptions.
 *  • This only overrides option sets present in draftDropdownOptionsBySet.
 *  • Untouched option sets remain from MultiAnswerFieldContext.
 */
const mergeDropdownOptionOverrides = ({
  baseDropdownOptions = {},
  draftDropdownOptionsBySet = {},
} = {}) => ({
  ...(baseDropdownOptions || {}),
  ...(draftDropdownOptionsBySet || {}),
});

const buildApplySignature = (applyPlan = {}) => {
  const optionSignature = (applyPlan.optionWrites || []).map((write) => ({
    groupId: write?.groupId || '',
    fieldId: write?.fieldId || '',
    optionsSetUFI: write?.optionsSetUFI || '',
    nextOptions: write?.nextOptions || [],
  }));

  const fieldMetadataSignature = (applyPlan.fieldMetadataWrites || []).map((write) => ({
    groupId: write?.groupId || '',
    fieldId: write?.fieldId || '',
    patch: write?.patch || {},
  }));

  return JSON.stringify({
    optionSignature,
    fieldMetadataSignature,
  });
};

const countDirtyRows = (dirtyState) => {
  const dirtyRowsByGroup = dirtyState?.dirtyRowsByGroup || {};

  return Object.values(dirtyRowsByGroup).reduce(
    (sum, rowSet) => (
      rowSet instanceof Set ? sum + rowSet.size : sum
    ),
    0,
  );
};

const countDirtyCells = (dirtyState) => {
  const dirtyCellsByGroup = dirtyState?.dirtyCellsByGroup || {};

  return Object.values(dirtyCellsByGroup).reduce(
    (groupSum, rowsMap) => (
      groupSum +
      Object.values(rowsMap || {}).reduce(
        (rowSum, cellSet) => (
          cellSet instanceof Set ? rowSum + cellSet.size : rowSum
        ),
        0,
      )
    ),
    0,
  );
};

const buildDirtySummary = (dirtyState) => {
  const dirtyGroupIds = dirtyState?.dirtyGroupIds;
  const dirtyGroupCount =
    dirtyGroupIds instanceof Set ? dirtyGroupIds.size : 0;

  return {
    dirtyGroupCount,
    dirtyRowCount: countDirtyRows(dirtyState),
    dirtyCellCount: countDirtyCells(dirtyState),
  };
};

const hasObjectKeys = (value) => (
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).length > 0
);

/**
 * useFormListsSpreadsheetSync
 * -----------------------------------------------------------------------------
 * V2 Form Lists sync layer between:
 *  • fast local GroupedSpreadsheetCanvas state
 *  • dependency side-panel freshness
 *  • dirty-only context/storage persistence
 *  • Save Draft / Publish flush boundaries
 *
 * ENTERPRISE RULES
 *  • Do not write every keystroke into global contexts.
 *  • Do not flush the entire canvas.
 *  • Dirty option sets only.
 *  • Side panel reads immediate draft overrides.
 *  • Save/Publish can force-flush before compression.
 *
 * WHY THIS IS NOT A PATCH
 *  • It formalizes the source-of-truth boundary:
 *      local spreadsheet draft → effective dropdownOptions view → dirty-only
 *      context/storage flush.
 *  • It follows the SmartLists pattern of keeping hot edits local and pulling
 *    snapshots at intentional boundaries.
 *  • It keeps the shared Spreadsheet/ engine generic.
 */
export const useFormListsSpreadsheetSync = ({
  formId,
  canvasRef,
  baseGroups = [],
  baseDropdownOptions = {},
  saveDropdownOptionsToStorage,
  updateFieldMetadata,
  updateField,
  registerFormListsFlush,
  markFormListsUnsavedWorkingCopy,
  showSnackbar,
} = {}) => {
  const baseDropdownOptionsRef = useRef(baseDropdownOptions || {});
  const draftDropdownOptionsBySetRef = useRef({});
  const lastAutosavedApplySignatureRef = useRef('');

  const localApplyInFlightRef = useRef(false);
  const pendingLocalApplyRef = useRef(false);

  const [dirtySummary, setDirtySummary] = useState(dirtySummaryInitialState);
  const [draftDropdownOptionsBySet, setDraftDropdownOptionsBySet] = useState({});
  const [isApplying, setIsApplying] = useState(false);
  const [lastApplyDiagnostics, setLastApplyDiagnostics] = useState(null);

  useEffect(() => {
    baseDropdownOptionsRef.current = baseDropdownOptions || {};
  }, [baseDropdownOptions]);

  /**
   * When context catches up to draft overrides, remove those overrides.
   *
   * WHY
   *  • Background/manual flush writes dirty option sets into
   *    MultiAnswerFieldContext.
   *  • Once MultiAnswerFieldContext reflects the same data, the draft override
   *    is no longer needed for side-panel freshness.
   */
  useEffect(() => {
    setDraftDropdownOptionsBySet((prev) => {
      if (!hasObjectKeys(prev)) return prev;

      let changed = false;
      const next = { ...prev };

      Object.entries(prev).forEach(([optionsSetUFI, draftOptions]) => {
        const baseOptions = baseDropdownOptions?.[optionsSetUFI];

        if (JSON.stringify(baseOptions || []) === JSON.stringify(draftOptions || [])) {
          delete next[optionsSetUFI];
          changed = true;
        }
      });

      if (!changed) return prev;

      draftDropdownOptionsBySetRef.current = next;
      return next;
    });
  }, [baseDropdownOptions]);

  const effectiveDropdownOptions = useMemo(() => (
    mergeDropdownOptionOverrides({
      baseDropdownOptions,
      draftDropdownOptionsBySet,
    })
  ), [
    baseDropdownOptions,
    draftDropdownOptionsBySet,
  ]);

  const buildMergedDropdownOptions = useCallback((draftOverrides = null) => (
    mergeDropdownOptionOverrides({
      baseDropdownOptions: baseDropdownOptionsRef.current,
      draftDropdownOptionsBySet: draftOverrides || draftDropdownOptionsBySetRef.current,
    })
  ), []);

  const getCurrentApplyPlan = useCallback(({
    dirtyOnly = true,
  } = {}) => {
    if (!canvasRef?.current) {
      return null;
    }

    /**
     * V2 sparse snapshot policy
     * -------------------------------------------------------------------------
     * Prefer the shared Spreadsheet engine's dirty-group snapshot API.
     *
     * WHY
     *  • Fix 1 stopped normal commits from writing global context/storage.
     *  • Fix 2 ensures the remaining draft-refresh/apply work does not first
     *    scan every group in the canvas.
     *  • Save Draft / Publish still force-flush dirty groups before compression.
     *
     * FALLBACK
     *  • getWorkspaceSnapshot(...) remains supported so this hook can still run
     *    safely if the canvas API is temporarily unavailable during rollout.
     */
    const canUseDirtySnapshot =
      dirtyOnly === true &&
      typeof canvasRef.current.getDirtyWorkspaceSnapshot === 'function';

    const snapshot = canUseDirtySnapshot
      ? canvasRef.current.getDirtyWorkspaceSnapshot({
          includeBlankRows: false,
        })
      : canvasRef.current.getWorkspaceSnapshot({
          includeBlankRows: false,
        });

    const applyPlan = buildFormListsApplyPlanFromSnapshot({
      snapshot,
      baseGroups,
      includeReadOnlyGroups: false,
      includeReadOnlyLabels: false,
      applyAllGroups: false,
    });

    return {
      snapshot,
      applyPlan,
    };
  }, [
    baseGroups,
    canvasRef,
  ]);

  /**
   * refreshDraftOverridesFromCanvas
   * ---------------------------------------------------------------------------
   * Pulls the latest dirty option sets from the canvas into a local draft map.
   *
   * IMPORTANT
   *  • This does NOT write context.
   *  • This does NOT write storage.
   *  • This is what makes the dependency side panel see latest spreadsheet rows
   *    immediately, without waiting for background autosave.
   *
   * ENTERPRISE PERFORMANCE RULE
   *  • Normal spreadsheet commits update the ref-backed draft override map only.
   *  • React state is updated only when a consumer explicitly needs to render
   *    from the override, such as the dependency side panel.
   *  • This keeps Enter/Tab navigation local and avoids rebuilding the page from
   *    global dropdownOptions context after every committed cell.
   */
  const refreshDraftOverridesFromCanvas = useCallback(({
    reason = 'refresh_draft_overrides',
    publishToReactState = true,
  } = {}) => {
    const current = getCurrentApplyPlan();

    if (!current?.applyPlan) {
      return {
        status: 'skipped',
        reason: 'missing_apply_plan',
        dropdownOptionsOverride: buildMergedDropdownOptions(),
      };
    }

    const { applyPlan } = current;
    const nextOverrides = {};

    (applyPlan.optionWrites || []).forEach((write) => {
      if (!write?.optionsSetUFI) return;

      nextOverrides[write.optionsSetUFI] = write.nextOptions || [];
    });

    if (!hasObjectKeys(nextOverrides)) {
      return {
        status: 'success',
        reason: 'no_dirty_option_overrides',
        diagnostics: applyPlan.diagnostics,
        dropdownOptionsOverride: buildMergedDropdownOptions(),
      };
    }

    const mergedOverrides = {
      ...draftDropdownOptionsBySetRef.current,
      ...nextOverrides,
    };

    draftDropdownOptionsBySetRef.current = mergedOverrides;

    if (publishToReactState) {
      setDraftDropdownOptionsBySet((prev) => {
        if (JSON.stringify(prev || {}) === JSON.stringify(mergedOverrides || {})) {
          return prev;
        }

        return mergedOverrides;
      });
    }

    return {
      status: 'success',
      reason,
      diagnostics: applyPlan.diagnostics,
      dropdownOptionsOverride: buildMergedDropdownOptions(mergedOverrides),
    };
  }, [
    buildMergedDropdownOptions,
    getCurrentApplyPlan,
  ]);

  const cancelScheduledApply = useCallback(() => {
    /**
     * V2 spreadsheet performance policy
     * -------------------------------------------------------------------------
     * Normal cell commits no longer schedule background context/storage flushes.
     *
     * WHY
     *  • Save Draft and Publish already force-flush Form Lists Lab edits through
     *    FormListsDraftSyncContext.
     *  • Manual Apply still uses flushDirtyChanges(...).
     *  • Keeping this function as a stable no-op preserves the existing cleanup
     *    and registered-flush call sites without reintroducing scheduled work.
     */
  }, []);

  useEffect(() => {
    return () => {
      cancelScheduledApply();
    };
  }, [cancelScheduledApply]);

  const handleDirtyStateChange = useCallback(({ dirtyState }) => {
    const nextSummary = buildDirtySummary(dirtyState);

    setDirtySummary((prev) => {
      if (
        prev.dirtyGroupCount === nextSummary.dirtyGroupCount &&
        prev.dirtyRowCount === nextSummary.dirtyRowCount &&
        prev.dirtyCellCount === nextSummary.dirtyCellCount
      ) {
        return prev;
      }

      return nextSummary;
    });
  }, []);

  const handleCellCommit = useCallback(() => {
    /**
     * Intentional no-op for V2 performance.
     * -------------------------------------------------------------------------
     * GroupedSpreadsheetCanvas already emits onCommitBoundary after a committed
     * cell edit, clear/delete, paste, or navigation commit.
     *
     * Keeping this callback stable prevents duplicate apply-plan/snapshot work:
     *  • onCellCommit should not refresh Form Lists draft overrides
     *  • onCellCommit should not write context
     *  • onCellCommit should not write storage
     *
     * Dependency side-panel freshness is handled by:
     *  • handleCommitBoundary ref refresh
     *  • FormListsLabPage dependency-panel-specific override refresh
     */
  }, []);

  /**
   * flushDirtyChanges
   * ---------------------------------------------------------------------------
   * Dirty-only persistence boundary.
   *
   * This is used by:
   *  • Apply list changes
   *  • Save Draft / Publish forced flush
   *
   * IMPORTANT
   *  • V2 dirty-group snapshop API is now in place.
   */
  const flushDirtyChanges = useCallback(async ({
    reason = 'manual',
    silent = false,
    clearDirtyOnSuccess = false,
    force = false,
    publishDraftOverridesToReactState = true,
  } = {}) => {
    if (!canvasRef?.current) {
      if (!silent && typeof showSnackbar === 'function') {
        showSnackbar('error', 'The Lists Lab canvas is not ready yet.');
      }

      return {
        status: 'skipped',
        reason: 'missing_canvas',
        dropdownOptionsOverride: buildMergedDropdownOptions(),
      };
    }

    if (localApplyInFlightRef.current) {
      pendingLocalApplyRef.current = true;

      return {
        status: 'queued',
        reason: 'apply_already_in_flight',
        dropdownOptionsOverride: buildMergedDropdownOptions(),
      };
    }

    localApplyInFlightRef.current = true;

    if (!silent) {
      setIsApplying(true);
    }

    try {
      let finalResult = {
        status: 'success',
        reason: 'nothing_to_apply',
        dropdownOptionsOverride: buildMergedDropdownOptions(),
      };

      do {
        pendingLocalApplyRef.current = false;

        /**
         * Active edit boundary
         * ---------------------------------------------------------------------
         * Before building the dirty snapshot, ask the shared spreadsheet canvas
         * to commit the currently active edit cell, if any.
         *
         * WHY
         *  • A user can type and immediately click another builder tab, Save
         *    Draft, or Publish before blur has completed.
         *  • The canvas remains domain-free; it only commits its local edit into
         *    localGroups/dirtyState.
         *  • The Form Lists adapter still owns what happens with the resulting
         *    dirty snapshot.
         */
        if (
          canvasRef?.current &&
          typeof canvasRef.current.flushPendingEdit === 'function'
        ) {
          await Promise.resolve(
            canvasRef.current.flushPendingEdit({
              reason,
            })
          );
        }

        const current = getCurrentApplyPlan();

        if (!current?.applyPlan) {
          finalResult = {
            status: 'skipped',
            reason: 'missing_apply_plan',
            dropdownOptionsOverride: buildMergedDropdownOptions(),
          };

          break;
        }

        const { applyPlan } = current;

        const nextOverrides = {};
        (applyPlan.optionWrites || []).forEach((write) => {
          if (!write?.optionsSetUFI) return;
          nextOverrides[write.optionsSetUFI] = write.nextOptions || [];
        });

        const mergedOverrides = {
          ...draftDropdownOptionsBySetRef.current,
          ...nextOverrides,
        };

        if (hasObjectKeys(nextOverrides)) {
          draftDropdownOptionsBySetRef.current = mergedOverrides;

          if (publishDraftOverridesToReactState) {
            setDraftDropdownOptionsBySet((prev) => {
              if (JSON.stringify(prev || {}) === JSON.stringify(mergedOverrides || {})) {
                return prev;
              }

              return mergedOverrides;
            });
          }
        }

        const dropdownOptionsOverride = buildMergedDropdownOptions(mergedOverrides);

        if (
          applyPlan.optionWrites.length === 0 &&
          applyPlan.fieldMetadataWrites.length === 0
        ) {
          finalResult = {
            status: 'success',
            reason: 'no_dirty_changes',
            diagnostics: applyPlan.diagnostics,
            dropdownOptionsOverride,
          };

          if (!silent) {
            setLastApplyDiagnostics(applyPlan.diagnostics);
            if (typeof showSnackbar === 'function') {
              showSnackbar('info', 'No dirty List changes to apply.');
            }
          }

          break;
        }

        const applySignature = buildApplySignature(applyPlan);

        if (
          !force &&
          silent &&
          applySignature === lastAutosavedApplySignatureRef.current
        ) {
          finalResult = {
            status: 'success',
            reason: 'autosave_signature_unchanged',
            diagnostics: applyPlan.diagnostics,
            dropdownOptionsOverride,
          };

          break;
        }

        const result = await applyFormListsApplyPlan({
          applyPlan,
          saveDropdownOptionsToStorage,
          updateFieldMetadata,
          updateField,
        });

        finalResult = {
          ...result,
          diagnostics: applyPlan.diagnostics,
          dropdownOptionsOverride,
        };

        if (result.status !== 'success') {
          if (!silent && typeof showSnackbar === 'function') {
            showSnackbar(
              'error',
              'Some List changes could not be applied. Check the console.',
            );
          }

          console.error('[useFormListsSpreadsheetSync] Apply result:', {
            reason,
            result,
          });

          break;
        }

        if (
          applyPlan.optionWrites.length > 0 &&
          typeof markFormListsUnsavedWorkingCopy === 'function'
        ) {
          markFormListsUnsavedWorkingCopy({
            formId,
            source: 'dropdownOptions',
            reason,
          });
        }

        lastAutosavedApplySignatureRef.current = applySignature;

        if (clearDirtyOnSuccess && !pendingLocalApplyRef.current) {
          canvasRef.current.clearDirtyState();
          lastAutosavedApplySignatureRef.current = '';
        }

        if (!silent) {
          setLastApplyDiagnostics({
            ...applyPlan.diagnostics,
            resultStatus: result.status,
          });

          if (typeof showSnackbar === 'function') {
            showSnackbar('success', 'List changes applied to form state.');
          }
        }
      } while (pendingLocalApplyRef.current);

      return finalResult;
    } catch (error) {
      console.error('[useFormListsSpreadsheetSync] Failed to apply List changes:', {
        reason,
        error,
      });

      if (!silent && typeof showSnackbar === 'function') {
        showSnackbar(
          'error',
          error?.message || 'Failed to apply List changes.',
        );
      }

      return {
        status: 'error',
        reason,
        error,
        dropdownOptionsOverride: buildMergedDropdownOptions(),
      };
    } finally {
      localApplyInFlightRef.current = false;

      if (!silent) {
        setIsApplying(false);
      }
    }
  }, [
    buildMergedDropdownOptions,
    canvasRef,
    formId,
    getCurrentApplyPlan,
    markFormListsUnsavedWorkingCopy,
    saveDropdownOptionsToStorage,
    showSnackbar,
    updateField,
    updateFieldMetadata,
  ]);

  const handleCommitBoundary = useCallback((commitEvent = {}) => {
    /**
     * V2 commit boundary policy
     * -------------------------------------------------------------------------
     * Keep committed spreadsheet edits local and fast.
     *
     * WHAT HAPPENS HERE
     *  • Pull latest dirty option-set writes from the canvas.
     *  • Store those writes in a ref-backed draft override map.
     *  • Return the merged dropdownOptions override to callers that need it.
     *
     * WHAT DOES NOT HAPPEN HERE
     *  • No MultiAnswerFieldContext writes.
     *  • No safeSetItem writes.
     *  • No Firestore writes.
     *  • No scheduled background flush after every Enter/Tab.
     *
     * WHY
     *  • Save Draft / Publish already force-flush through the registered
     *    Form Lists flusher.
     *  • Dependency settings can request the returned override immediately.
     *  • This matches spreadsheet-grade behavior: local edits first, durable
     *    persistence at intentional boundaries.
     */
    return refreshDraftOverridesFromCanvas({
      reason: commitEvent?.source || 'spreadsheet_commit_boundary',
      publishToReactState: false,
    });
  }, [
    refreshDraftOverridesFromCanvas,
  ]);

  const handleApplyChanges = useCallback(async () => {
    cancelScheduledApply();
    pendingLocalApplyRef.current = false;

    return await flushDirtyChanges({
      reason: 'manual_apply_button',
      silent: false,
      clearDirtyOnSuccess: true,
      force: true,
    });
  }, [
    cancelScheduledApply,
    flushDirtyChanges,
  ]);

  useEffect(() => {
    if (!formId) return undefined;
    if (typeof registerFormListsFlush !== 'function') return undefined;

    return registerFormListsFlush({
      id: 'form-lists-lab-page',
      formId,
      flush: async ({ reason = 'registered_flush' } = {}) => {
        cancelScheduledApply();

        return await flushDirtyChanges({
          reason,
          silent: true,
          clearDirtyOnSuccess: true,
          force: true,
        });
      },
    });
  }, [
    cancelScheduledApply,
    flushDirtyChanges,
    formId,
    registerFormListsFlush,
  ]);

  return {
    dirtySummary,
    isApplying,
    lastApplyDiagnostics,
    effectiveDropdownOptions,
    handleDirtyStateChange,
    handleCellCommit,
    handleCommitBoundary,
    handleApplyChanges,
    flushDirtyChanges,
    refreshDraftOverridesFromCanvas,
  };
};

export default useFormListsSpreadsheetSync;